import { ArrowRight, Camera, Check, Layers3, Search, ShieldCheck, Store, SunMedium } from "lucide-react";
import { Link } from "react-router-dom";
import { BeforeAfter } from "../components/before-after";
import { Disclaimer } from "../components/disclaimer";
import { Logo } from "../components/logo";
import { ResponsiveImage } from "../components/responsive-image";
import { SiteHeader } from "../components/site-header";
import { products } from "../lib/catalog";

const formatEuro = (cents: number) =>
  new Intl.NumberFormat("hr-HR", { style: "currency", currency: "EUR" }).format(cents / 100);

export function LandingPage() {
  const previewProducts = products.filter((product) =>
    ["sofa", "coffee-table", "rug"].includes(product.category),
  );

  return (
    <div className="landing">
      <SiteHeader />
      <main id="main">
        <section className="hero" aria-labelledby="hero-title">
          <div className="hero__copy">
            <p className="hero__kicker">Interijer i eksterijer · bez registracije</p>
            <h1 id="hero-title">Uredi prostor. Pronađi gdje to možeš kupiti.</h1>
            <p className="hero__lead">
              Fotografiraj interijer ili eksterijer, dobij pet AI prijedloga i pronađi slične proizvode u
              hrvatskim trgovinama.
            </p>
            <div className="button-row">
              <Link className="button button--primary" to="/app/new">
                Uredi svoj prostor
                <ArrowRight aria-hidden="true" />
              </Link>
              <a className="text-link" href="#kako-radi">
                Pogledaj kako radi
              </a>
            </div>
            <ul className="hero__assurances" aria-label="Prednosti">
              <li>
                <Check aria-hidden="true" /> 5 smjerova
              </li>
              <li>
                <Check aria-hidden="true" /> Hrvatski katalog
              </li>
              <li>
                <Check aria-hidden="true" /> Brisanje nakon 7 dana
              </li>
            </ul>
          </div>
          <div className="hero__visual">
            <BeforeAfter />
            <div className="match-preview" aria-label="Primjer pronađenog proizvoda">
              <div className="match-preview__intro">
                <span className="measure-mark">Ø 90 cm</span>
                <strong>Pronađeno za ovaj prijedlog</strong>
              </div>
              <img src="/product-images/coffee-table.svg" alt="" width="152" height="108" />
              <div>
                <span className="match-level">Vrlo slično</span>
                <p>Stolić Tera</p>
                <strong>{formatEuro(15900)}</strong>
              </div>
              <span className="match-preview__demo">Demo podatak</span>
            </div>
          </div>
        </section>

        <section className="value-ribbon" aria-label="Ključne vrijednosti">
          <div>
            <Layers3 aria-hidden="true" />
            <span>
              <strong>Pet prijedloga,</strong> ne jedan nasumičan rezultat
            </span>
          </div>
          <div>
            <Store aria-hidden="true" />
            <span>
              <strong>Stvarne mjere i cijene</strong> iz demo hrvatskog kataloga
            </span>
          </div>
          <div>
            <SunMedium aria-hidden="true" />
            <span>
              <strong>Interijer i eksterijer</strong> u istom jasnom flowu
            </span>
          </div>
        </section>

        <section id="kako-radi" className="how section">
          <div className="section-heading">
            <p className="section-label">Od fotografije do konkretnog popisa</p>
            <h2>Ideja prestaje biti samo spremljena slika.</h2>
            <p>
              ProstorAI povezuje vizualni smjer s elementima koje možeš mjeriti, usporediti i pronaći u
              trgovini.
            </p>
          </div>
          <ol className="process-list">
            <li>
              <span className="process-list__icon">
                <Camera aria-hidden="true" />
              </span>
              <div>
                <strong>Fotografiraj prostor</strong>
                <p>Učitaj fotografiju s mobitela ili računala. Možeš je ukloniti u bilo kojem trenutku.</p>
              </div>
            </li>
            <li>
              <span className="process-list__icon">
                <Layers3 aria-hidden="true" />
              </span>
              <div>
                <strong>Odaberi što želiš promijeniti</strong>
                <p>Stil, budžet, boje, što ostaje i što odlazi daju svakom smjeru praktičan okvir.</p>
              </div>
            </li>
            <li>
              <span className="process-list__icon">
                <Search aria-hidden="true" />
              </span>
              <div>
                <strong>Pregledaj pet prijedloga</strong>
                <p>Usporedi original i rezultat te odaberi smjer koji najbolje odgovara prostoru.</p>
              </div>
            </li>
            <li>
              <span className="process-list__icon">
                <Store aria-hidden="true" />
              </span>
              <div>
                <strong>Usporedi slične proizvode</strong>
                <p>Match objašnjava boju, stil, mjeru, materijal i odnos prema budžetu.</p>
              </div>
            </li>
          </ol>
        </section>

        <section className="category-stage section" aria-labelledby="category-title">
          <div className="section-heading section-heading--wide">
            <p className="section-label">Jedan alat, dvije vrste prostora</p>
            <h2 id="category-title">Od dnevnog boravka do fasade.</h2>
          </div>
          <div className="category-split">
            <article id="interijer" className="category-panel category-panel--interior">
              <ResponsiveImage
                name="interior-after"
                alt="Topao mediteranski dnevni boravak"
                sizes="(max-width: 800px) 100vw, 50vw"
              />
              <div className="category-panel__overlay">
                <span>Interijer</span>
                <h3>Namještaj, boje i raspored koji poštuju postojeći prostor.</h3>
                <Link to="/app/new?kind=interior">
                  Pokreni interijer <ArrowRight aria-hidden="true" />
                </Link>
              </div>
            </article>
            <article id="eksterijer" className="category-panel category-panel--exterior">
              <ResponsiveImage
                name="exterior-after"
                alt="Obnovljena mediteranska kuća i vrt"
                sizes="(max-width: 800px) 100vw, 50vw"
              />
              <div className="category-panel__overlay">
                <span>Eksterijer</span>
                <h3>Fasada, terasa, ograda i vrt u izvedivom zajedničkom smjeru.</h3>
                <Link to="/app/new?kind=exterior">
                  Pokreni eksterijer <ArrowRight aria-hidden="true" />
                </Link>
              </div>
            </article>
          </div>
        </section>

        <section className="demo-result section">
          <div className="demo-result__visual">
            <ResponsiveImage
              name="interior-after"
              alt="AI prijedlog mediteranskog dnevnog boravka"
              sizes="(max-width: 900px) 100vw, 58vw"
            />
            <span className="detected-tag detected-tag--sofa">Sofa · 238 cm</span>
            <span className="detected-tag detected-tag--table">Stolić · Ø 90 cm</span>
            <span className="detected-tag detected-tag--light">Visilica · Ø 60 cm</span>
          </div>
          <div className="demo-result__content">
            <p className="section-label">Demo rezultat</p>
            <h2>Vizualizacija koja završava usporedbom.</h2>
            <p>Svaki prepoznati element vodi prema rangiranim proizvodima i jasnom objašnjenju matcha.</p>
            <div className="mini-product-list">
              {previewProducts.slice(0, 3).map((product, index) => (
                <article key={product.id}>
                  <img src={product.imageUrl} alt="" width="112" height="78" />
                  <div>
                    <span>{["Vrlo slično", "Slično", "Inspirirano"][index]}</span>
                    <strong>{product.title}</strong>
                    <small>
                      {product.store} · {formatEuro(product.priceCents)}
                    </small>
                  </div>
                </article>
              ))}
            </div>
            <Disclaimer />
            <Link className="button button--primary" to="/app/new">
              Isprobaj na svom prostoru
              <ArrowRight aria-hidden="true" />
            </Link>
          </div>
        </section>

        <section className="trust-section">
          <ShieldCheck aria-hidden="true" />
          <div>
            <p className="section-label">Privatnost bez fusnote</p>
            <h2>Tvoja fotografija nije trajna.</h2>
          </div>
          <p>
            Projekt možeš obrisati odmah. U produkciji se fotografije automatski brišu nakon sedam dana, a
            udaljene slike trgovina ne koriste se kao AI input.
          </p>
        </section>

        <section id="pitanja" className="faq section">
          <div className="section-heading">
            <p className="section-label">Česta pitanja</p>
            <h2>Prije prve fotografije.</h2>
          </div>
          <div className="faq-list">
            <details>
              <summary>Jesu li proizvodi identični onima na AI vizualizaciji?</summary>
              <p>Ne. Prikazujemo vizualno i funkcionalno slične opcije te objašnjavamo razinu podudaranja.</p>
            </details>
            <details>
              <summary>Radi li bez registracije?</summary>
              <p>Da. MVP koristi anonimnu sesiju i čuva jedan aktivni projekt.</p>
            </details>
            <details>
              <summary>Je li ovo arhitektonska ili građevinska procjena?</summary>
              <p>Ne. Rezultat je prijedlog izgleda, a zahvate uvijek treba provjeriti sa stručnom osobom.</p>
            </details>
            <details>
              <summary>Odakle dolaze cijene?</summary>
              <p>
                U demo načinu iz jasno označenog lokalnog kataloga. Produkcijski izvori zahtijevaju provjeru.
              </p>
            </details>
          </div>
        </section>

        <section className="final-cta">
          <div>
            <p>Fotkaj prostor. Pogledaj kako može izgledati.</p>
            <h2>Od ideje do prvog konkretnog izbora.</h2>
          </div>
          <Link className="button button--light" to="/app/new">
            Uredi svoj prostor
            <ArrowRight aria-hidden="true" />
          </Link>
        </section>
      </main>
      <footer className="site-footer">
        <Logo inverse />
        <p>ProstorAI je demo MVP. Vizualizacije nisu stručne građevinske procjene.</p>
        <nav aria-label="Podnožje">
          <a href="/uvjeti">Uvjeti</a>
          <a href="/privatnost">Privatnost</a>
          <a href="mailto:pozdrav@prostorai.hr">Kontakt</a>
          <a href="mailto:trgovine@prostorai.hr">Za trgovine</a>
        </nav>
      </footer>
    </div>
  );
}
